import type { ComplianceStatus } from '@mvcp/contracts';

/**
 * Certificate/notification-status → pill tone + label, shared across the cockpit
 * (Story 4.4 CertificateRows, Story 4.5 NotificationBell). Inlined locally: the web
 * consumes `@mvcp/contracts` as TYPES ONLY — a runtime-value import breaks
 * `vite build` (Story 1.6). Groups the canonical {@link ComplianceStatus} set into
 * the four pill tones:
 *   `active|approved|verified` → ok · `expiring|pending|requested` → warn ·
 *   `expired|missing|unverified` → bad · `na|not_assessed` → neutral.
 */
export const STATUS_TONE: Record<ComplianceStatus, 'ok' | 'warn' | 'bad' | 'neutral'> = {
  active: 'ok',
  approved: 'ok',
  verified: 'ok',
  expiring: 'warn',
  pending: 'warn',
  requested: 'warn',
  expired: 'bad',
  missing: 'bad',
  unverified: 'bad',
  na: 'neutral',
  not_assessed: 'neutral',
};

/**
 * Human status label — the SOURCE OF TRUTH beside every colored dot (status is
 * never color alone). One label per canonical {@link ComplianceStatus}.
 */
export const STATUS_LABEL: Record<ComplianceStatus, string> = {
  active: 'Active',
  approved: 'Approved',
  verified: 'Verified',
  expiring: 'Expiring',
  pending: 'Pending',
  requested: 'Requested',
  expired: 'Expired',
  missing: 'Missing',
  unverified: 'Unverified',
  na: 'N/A',
  not_assessed: 'Not assessed',
};

/** A status pill with a leading dot AND a text label (never color alone). */
export function StatusPill({ status }: { status: ComplianceStatus }): React.JSX.Element {
  return (
    <span className="status-pill" data-tone={STATUS_TONE[status]}>
      <span className="status-pill__dot" aria-hidden="true" />
      {STATUS_LABEL[status]}
    </span>
  );
}
